// src/pages/Welcome.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/config";
import { ROUTES } from "../constants";
import { Button } from "../components/common/Button";
import { Loading } from "../components/common/Loading";
import apiService from "../services/api.service";
import "../assets/css/welcome.css";

import {
  FaClipboard,
  FaCheckCircle,
  FaUsers,
  FaClock,
  FaBell,
  FaExchangeAlt,
  FaComments,
} from 'react-icons/fa';

export default function Welcome() {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [publicStats, setPublicStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate(ROUTES.HOME);
      } else {
        apiService
          .getPublicDashboard()
          .then((data) => {
            setPublicStats({
              totalReclamos: data.totalReclamos ?? 0,
              reclamosResueltos: data.reclamosResueltos ?? 0,
              usuariosRegistrados: data.usuariosRegistrados ?? 0,
              tiempoPromedioResolucionDias:
                data.tiempoPromedioResolucionDias?.toFixed(1) ?? "N/A",
            });
            setCheckingAuth(false);
          })
          .catch((err) => {
            console.error("Error cargando estadísticas públicas:", err);
            setCheckingAuth(false);
          });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (checkingAuth) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-body)"
      }}>
        <Loading text="Verificando sesión..." />
      </div>
    );
  }

  return (
    <div className="welcome-page">
      {/* HERO */}
      <section className="hero-section">
        <div className="hero-container">
          {/* Texto principal */}
          <div className="hero-text">
            <h1 className="hero-title">
              Sistema de Gestión de Reclamos Ciudadanos
            </h1>
            <p className="hero-subtitle">
              Un puente directo entre la Municipalidad y los vecinos.
              Registrá tus reclamos, hacé seguimiento y contribuí a mejorar tu ciudad.
            </p>

            <div className="hero-actions">
              <button
                className="btn-hero btn-hero-primary"
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                Iniciar Sesión
              </button>

              <button
                className="btn-hero btn-hero-secondary"
                onClick={() => navigate(ROUTES.REGISTER)}
              >
                Crear Cuenta
              </button>
            </div>
          </div>

          {/* Imagen institucional */}
          <div className="hero-image-container">
            <img
              src={""}
              alt="Municipalidad"
              className="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <div className="section section-bg-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Plataforma Digital Municipal
            </h2>
            <p className="section-subtitle">
              Gestión eficiente de reportes ciudadanos con seguimiento en tiempo real y transparencia total.
            </p>
          </div>

          <div className="features-grid">
            {[
              {
                icon: <FaBell />,
                title: "Reportes de Emergencia",
                description: "Sistema prioritario para situaciones que requieren atención inmediata"
              },
              {
                icon: <FaExchangeAlt />,
                title: "Seguimiento Transparente",
                description: "Visualiza el estado de tu denuncia desde el reporte hasta la resolución"
              },
              {
                icon: <FaComments />,
                title: "Comunicación Directa",
                description: "Canal directo con las autoridades municipales para resolver problemas"
              }
            ].map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon-wrapper">
                  {feature.icon}
                </div>
                <h3 className="feature-title">
                  {feature.title}
                </h3>
                <p className="feature-text">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Public Stats Section */}
      <div className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Estadísticas Públicas</h2>
            <p className="section-subtitle">Transparencia en números</p>
          </div>

          <div className="stats-grid">
            {publicStats ? (
              <>
                {[
                  { label: "Reclamos Totales", value: publicStats.totalReclamos, icon: <FaClipboard />, color: "#3b82f6" },
                  { label: "Reclamos Resueltos", value: publicStats.reclamosResueltos, icon: <FaCheckCircle />, color: "#10b981" },
                  { label: "Usuarios Registrados", value: publicStats.usuariosRegistrados, icon: <FaUsers />, color: "#f59e0b" },
                  { label: "Promedio Resolución", value: `${publicStats.tiempoPromedioResolucionDias} días`, icon: <FaClock />, color: "#6366f1" },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="stat-card"
                    style={{ color: stat.color }}
                  >
                    <div className="stat-icon">{stat.icon}</div>
                    <h3 className="stat-label">{stat.label}</h3>
                    <p className="stat-value">
                      {stat.value ?? "—"}
                    </p>
                  </div>
                ))}
              </>
            ) : (
              <p style={{ color: "var(--text-light)", textAlign: "center", gridColumn: "1 / -1" }}>Cargando estadísticas...</p>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="section section-bg-light">
        <div className="container">
          <div className="cta-container">
            <h3 className="cta-title">
              ¿Listo para hacer la diferencia en tu comunidad?
            </h3>
            <p className="section-subtitle" style={{ marginBottom: "32px" }}>
              Únete a cientos de ciudadanos que ya utilizan nuestra plataforma.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(ROUTES.REGISTER)}
              style={{
                fontSize: "1.1rem",
                padding: "16px 40px"
              }}
            >
              Comenzar Ahora
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <p>
          © 2025 Municipalidad Digital • Transparencia • Eficiencia • Participación Ciudadana
        </p>
      </footer>
    </div>
  );
}
